import React, { FormEvent, useEffect, useReducer, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import { text, unitMap } from '../data';
import { getBearingSpringRates } from '../../components/bearing-search/api/api';
import { bearingSpringRateChartReducer, BearingSpringRatesState, initialBearingSpringRateChartState, XAxisTypes, YAxisTypes } from './reducer';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import BearingSpringRatesChart from './BearingSpringRatesChart';
import { BearingPartState } from '../../components/bearing-search/reducers/reducer';
import { XAxisLimit, YAxisLimit } from "../types/types";
import { trackToolActions } from '../../analytics/engineering-tools';

Chart.register(...registerables);

interface BearingSpringRatesChartFormProps {
	data: Array<{ load: number; deflection: number }>;
	xAxisLabel: string;
	yAxisLabel: string;
	title: string;
	xAxisMin?: number;
	xAxisMax?: number;
	yAxisMin?: number;
	yAxisMax?: number;
	yAxisScaling: boolean;
	yAxisType?: string;
	loading?: boolean;
	xAxisLimits?: XAxisLimit;
	yAxisLimits?: YAxisLimit;
	partState?: BearingPartState;
	formState?: BearingSpringRatesState;
	complete?: boolean;
}

const BearingSpringRatesChartForm: React.FC<BearingSpringRatesChartFormProps> = ({
	loading,
	partState,
	formState,
	xAxisLabel: xLabel,
	yAxisLabel: yLabel,
	xAxisLimits,
	yAxisLimits,
	complete
}) => {
	const [chartState, chartDispatch] = useReducer(bearingSpringRateChartReducer, initialBearingSpringRateChartState);
	const [yAxisScaling, setYAxisScaling] = useState(false);
	const [xRadioType, setXRadioType] = useState('');
	const [yRadioType, setYRadioType] = useState('');
	const [xAxisLabel, setXAxisLabel] = useState('');
	const [yAxisLabel, setYAxisLabel] = useState('');
	const [canShowGraph, setCanShowGraph] = useState(false);

	// On first load, set values for X and Y Labels to ensure "Plot" button won't be disabled if you don't touch these labels manually
	useEffect(() => {
		setXAxisLabel(xLabel);
		setXRadioType(XAxisTypes.radial);
		chartDispatch({
			type: 'set_x_axis_label',
			payload: xLabel,
		});

		setYAxisLabel(yLabel);
		setYRadioType(YAxisTypes.deflection);
		chartDispatch({
			type: 'set_y_axis_label',
			payload: yLabel,
		});

		// Set Default Values for X-axis min/max and Y-axis min/max
		updateMinMaxValuesForAxis(XAxisTypes.radial, YAxisTypes.deflection, 'x');
	}, [])

	useEffect(() => {
		if (complete) {
			// Reset values when BearingSpringRates form is being submitted again and axial/radial data has been prepared from the API.
			chartDispatch({
				type: 'reset',
			});

			// Make sure to reset X and Y radio types to ensure Grid is populated correctly. Also reset Y axis scaling
			/**
			 * Set values back to initial:
			 * - XRadioType
			 * - YRadioType
			 * - YAxisScaling
			 * - XAxisLabel
			 * - YAxisLabel
			 * - CanShowGraph
			 */
			setXRadioType(XAxisTypes.radial);
			setYRadioType(YAxisTypes.deflection);
			setYAxisScaling(false);
			setCanShowGraph(false);

			setXAxisLabel(xLabel);
			chartDispatch({
				type: 'set_x_axis_label',
				payload: xLabel,
			});

			setYAxisLabel(yLabel);
			chartDispatch({
				type: 'set_y_axis_label',
				payload: yLabel,
			});

			updateMinMaxValuesForAxis(XAxisTypes.radial, YAxisTypes.deflection, 'x');
		}
	}, [complete]);

	const clearSearch = () => {
		// Reset ChartState
		chartDispatch({ type: 'set_y_axis_scaling', payload: 0 });
		chartDispatch({ type: 'set_y_axis_type', payload: 'deflection' });
		chartDispatch({ type: 'set_graph_type', payload: 'radial' });
		chartDispatch({ type: 'set_x_axis_label', payload: xLabel });
		chartDispatch({ type: 'set_y_axis_label', payload: yLabel });
		chartDispatch({ type: 'set_graph_title', payload: '' });
		chartDispatch({ type: 'change_number_field', payload: ['xAxisMin', xAxisLimits.radial.min] });
		chartDispatch({ type: 'change_number_field', payload: ['xAxisMax', xAxisLimits.radial.max] });
		chartDispatch({ type: 'change_number_field', payload: ['yAxisMin', yAxisLimits.radial.deflection.min] });
		chartDispatch({ type: 'change_number_field', payload: ['yAxisMax', yAxisLimits.radial.deflection.max] });

		// Reset additional attributes
		setCanShowGraph(false);
		setXAxisLabel(xLabel);
		setXRadioType(XAxisTypes.radial);
		setYRadioType(YAxisTypes.deflection);
		setYAxisLabel(yLabel);
		setYAxisScaling(false);
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault(); // Prevent the default form submission behavior
		const payload = {
			bearingType: partState?.bearingType,
			bearingSubType: partState?.bearingSubType,
			partNumber: partState?.partNumber,
			cupPartNumber: partState?.cupPartNumber,
			unitSystem: formState?.unitSystem,
			preload: Boolean(parseInt(formState?.springRatesPreload)),
			isReportRequired: true,
		};

		const data = await getBearingSpringRates(payload);

		chartDispatch({
			type: 'set_axial_graph_data',
			payload: data.standardSpringRate.axial,
		});

		chartDispatch({
			type: 'set_radial_graph_data',
			payload: data.standardSpringRate.radial,
		});

		setCanShowGraph(true);

		trackToolActions('et_generate_plot_graph', 'bearing spring reates');
	};

	const isPlotDisabled = () => {
		return (
			chartState.xAxisMin === undefined ||
			chartState.xAxisMax === undefined ||
			chartState.yAxisMin === undefined ||
			chartState.yAxisMax === undefined ||
			!chartState.xAxisLabel ||
			!chartState.yAxisLabel ||
			loading
		);
	};

	const handleYAxisScalingChange = () => {
		setYAxisScaling(!yAxisScaling);
		chartDispatch({
			type: 'set_y_axis_scaling',
			payload: !yAxisScaling,
		})
	}

	const handleXAxisLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let label = e.target.value;
		chartDispatch({
			type: 'set_x_axis_label',
			payload: label,
		});
		setXAxisLabel(label);
	};

	const handleYAxisLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let label = e.target.value;
		chartDispatch({
			type: 'set_y_axis_label',
			payload: label,
		});
		setYAxisLabel(label);
	};

	const handleXTypeRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let type = e.target.value;
		chartDispatch({
			type: 'set_graph_type',
			payload: type,
		});
		setXRadioType(type);

		updateMinMaxValuesForAxis(type, chartState.yAxisType, 'x');

		// Need to set the X Axis Label as well as the Y Axis Label as the Y Axis label is dependent on the X Axis Type
		const targetType = type.charAt(0).toUpperCase() + type.slice(1);
		const targetYProperty = chartState.yAxisType === YAxisTypes.deflection ? 'Deflection' : 'Spring Rates';
		const targetYLabel = `${targetType} ${targetYProperty}`;
		const preloadLabel = formState?.springRatesPreload == 1 && type == XAxisTypes.radial ? `(with preload)` : '';
		const loadLabel = `${targetType} Load ${preloadLabel}`;

		chartDispatch({
			type: 'set_x_axis_label',
			payload: loadLabel,
		});

		chartDispatch({
			type: 'set_y_axis_label',
			payload: targetYLabel,
		});

		setYAxisLabel(targetYLabel);
		setXAxisLabel(loadLabel);
	}

	const handleYTypeRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let type = e.target.value;
		chartDispatch({
			type: 'set_y_axis_type',
			payload: type,
		});
		setYRadioType(type);

		updateMinMaxValuesForAxis(chartState.graphType, type, 'y');

		const targetType = chartState.graphType.charAt(0).toUpperCase() + chartState.graphType.slice(1);
		const targetYProperty = type === YAxisTypes.deflection ? 'Deflection' : 'Spring Rates';
		const targetYLabel = `${targetType} ${targetYProperty}`;

		chartDispatch({
			type: 'set_y_axis_label',
			payload: targetYLabel,
		});

		setYAxisLabel(targetYLabel);
	}

	const updateMinMaxValuesForAxis = (xType: string, yType: string, radioType: string) => {
		if (xType === XAxisTypes.radial) {
			// Only need to update xAxis min/max values when updating X Axis Type
			if (radioType === 'x') {
				// Update X Axis Min/Max values
				chartDispatch({
					type: 'change_number_field',
					payload: ['xAxisMin', xAxisLimits.radial.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['xAxisMax', xAxisLimits.radial.max],
				});
			}
			// Update Y Axis Min/Max Values
			if (yType === YAxisTypes.deflection) {
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMin', yAxisLimits.radial.deflection.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMax', yAxisLimits.radial.deflection.max],
				});
			} else {
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMin', yAxisLimits.radial.springRate.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMax', yAxisLimits.radial.springRate.max],
				});
			}
		} else {
			// Only need to update xAxis min/max values when updating X Axis Type
			if (radioType === 'x') {
				// Update X Axis Min/Max values
				chartDispatch({
					type: 'change_number_field',
					payload: ['xAxisMin', xAxisLimits.axial.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['xAxisMax', xAxisLimits.axial.max],
				});
			}
			// Update Y Axis Min/Max Values
			if (yType === YAxisTypes.deflection) {
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMin', yAxisLimits.axial.deflection.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMax', yAxisLimits.axial.deflection.max],
				});
			} else {
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMin', yAxisLimits.axial.springRate.min],
				});
				chartDispatch({
					type: 'change_number_field',
					payload: ['yAxisMax', yAxisLimits.axial.springRate.max],
				});
			}
		}
	}

	return (
		<div className="graph-and-form-container">
			{!loading && (
				<>
					<form onSubmit={handleSubmit}>
						<div className="form-section">
							<h3>Generate graph</h3>

							<span>
								<p className='section-title'>X-Axis</p>
							</span>
							<div className="subsection">
								<div className='subsection-options'>
									<div className="m-radio">
										<div className="m-radio__field">
											<input
												id='xTypeRadial'
												className="m-radio__input"
												type="radio"
												name="xAxis"
												value="radial"
												checked={chartState.graphType === XAxisTypes.radial}
												onChange={(e) => handleXTypeRadioChange(e)}
											/>
											<label className="m-radio__label" htmlFor='xTypeRadial'>
												Radial Load
											</label>
										</div>
									</div>
									<div className="m-radio">
										<div className="m-radio__field">
											<input
												id='xTypeAxial'
												className="m-radio__input"
												type="radio"
												name="xAxis"
												value="axial"
												checked={chartState.graphType === XAxisTypes.axial}
												onChange={(e) => handleXTypeRadioChange(e)}
											/>
											<label className="m-radio__label" htmlFor='xTypeAxial'>
												Axial Load
											</label>
										</div>
									</div>
								</div>
								<div className="subsection-inputs">
									<div className="input-group">
										<NumberInput
											id="xAxisMin"
											required={true}
											label="Min"
											unitLabel={unitMap[formState.unitSystem].force}
											value={chartState.xAxisMin}
											min={chartState.graphType === XAxisTypes.radial ? xAxisLimits.radial.min : xAxisLimits.axial.min}
											max={chartState.graphType === XAxisTypes.radial ? xAxisLimits.radial.max : xAxisLimits.axial.max}
											onChange={(e) => {
												chartDispatch({
													type: 'change_number_field',
													payload: ['xAxisMin', e.target.value],
												});
											}}
										/>
									</div>
									<div className="input-group">
										<NumberInput
											id="xAxisMax"
											required={true}
											label="Max"
											min={chartState.xAxisMin}
											max={chartState.graphType === XAxisTypes.radial ? xAxisLimits.radial.max : xAxisLimits.axial.max}
											unitLabel={unitMap[formState.unitSystem].force}
											value={chartState.xAxisMax}
											onChange={(e) => {
												chartDispatch({
													type: 'change_number_field',
													payload: ['xAxisMax', e.target.value],
												});
											}}
										/>
									</div>
								</div>
							</div>
							<span>
								<p className='section-title'>Y-Axis</p>
							</span>
							<div className="subsection">
								<div className='subsection-options'>
									<div className="m-radio">
										<div className="m-radio__field" key={'yAxisTypeKey'}>
											<input
												id='yAxisDeflection'
												className="m-radio__input"
												checked={chartState.yAxisType === YAxisTypes.deflection}
												type="radio"
												name="yAxis"
												value={'deflection'}
												onChange={(e) => handleYTypeRadioChange(e)}
											/>
											<label className="m-radio__label" htmlFor='yAxisDeflection'>
												Deflection
											</label>
										</div>
									</div>
									<div className="m-radio">
										<div className="m-radio__field">
											<input
												id='yAxisSpringRate'
												className="m-radio__input"
												checked={chartState.yAxisType === YAxisTypes.springRate}
												type="radio"
												name="yAxis"
												value={'springRate'}
												onChange={(e) => handleYTypeRadioChange(e)}
											/>
											<label className="m-radio__label" htmlFor='yAxisSpringRate'>
												Spring Rates
											</label>
										</div>
									</div>
								</div>
								<div className="subsection-inputs">
									<div className="input-group">
										<NumberInput
											id="yAxisMin"
											required={true}
											label="Min"
											min={chartState.yAxisType === YAxisTypes.springRate
												? chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.springRate.min : yAxisLimits.axial.springRate.min
												: chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.deflection.min : yAxisLimits.axial.deflection.min}
											max={chartState.yAxisType === YAxisTypes.springRate
												? chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.springRate.max : yAxisLimits.axial.springRate.max
												: chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.deflection.max : yAxisLimits.axial.deflection.max}
											unitLabel={chartState.yAxisType === YAxisTypes.springRate
												? unitMap[formState.unitSystem].newtonLength
												: unitMap[formState.unitSystem].length
											}
											value={chartState.yAxisMin}
											onChange={(e) => {
												chartDispatch({
													type: 'change_number_field',
													payload: ['yAxisMin', e.target.value],
												});
											}}
										/>
									</div>
									<div className="input-group">
										<NumberInput
											id="yAxisMax"
											required={true}
											label="Max"
											min={chartState.yAxisMin}
											max={chartState.yAxisType === YAxisTypes.springRate
												? chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.springRate.max : yAxisLimits.axial.springRate.max
												: chartState.graphType === XAxisTypes.radial ? yAxisLimits.radial.deflection.max : yAxisLimits.axial.deflection.max}
											unitLabel={chartState.yAxisType === YAxisTypes.springRate
												? unitMap[formState.unitSystem].newtonLength
												: unitMap[formState.unitSystem].length
											}
											value={chartState.yAxisMax}
											onChange={(e) => {
												chartDispatch({
													type: 'change_number_field',
													payload: ['yAxisMax', e.target.value],
												});
											}}
										/>
									</div>
								</div>
							</div>
							<div>
								<span>
									<p className='section-title'>Y-Axis Scaling</p>
								</span>
							</div>
							<div className="subsection">
								<div className='subsection-options'>
									<div className="m-checkbox">
										<input
											id='yScalingLog'
											className="m-checkbox__input"
											type="checkbox"
											name="yAxisScaling"
											onChange={handleYAxisScalingChange}
											checked={yAxisScaling}
										/>
										<label className="m-checkbox__label" htmlFor='yScalingLog'>
											Semi Log (log 10)
										</label>
									</div>
								</div>
							</div>
							<div className={'graph-labels-section'}>
								<div className="col-12">
									<TextInput
										id="graphTitle"
										label="Title:"
										value={chartState.graphTitle}
										onChange={(e) => chartDispatch({
											type: 'set_graph_title',
											payload: e.target.value,
										})}
										placeholder="Title"
										maxLength={60}
									/>
								</div>
								<span className="row">
									<div className="col-6">
										<TextInput
											id="xAxisLabel"
											label="X-Axis Label"
											required={true}
											value={xAxisLabel}
											onChange={(e) => handleXAxisLabelChange(e)}
											placeholder="X-Axis label"
										/>
									</div>
									<div className="col-6">
										<TextInput
											id="yAxisLabel"
											label="Y-Axis Label"
											required={true}
											value={yAxisLabel}
											onChange={(e) => handleYAxisLabelChange(e)}
											placeholder="Y-Axis label"
										/>
									</div>
								</span>
							</div>
							<div className='button-section'>
								<button
									className="btn btn--primary"
									type="submit"
									disabled={isPlotDisabled()}
								>
									{text.plot}
								</button>
								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => {
										clearSearch();
									}}
								>
									{text.clear}
								</button>
							</div>
						</div>
					</form>

					{/* Graph Section */}
					{!!canShowGraph && (
						<div className="graph-section">
							<BearingSpringRatesChart
								cupNumber={partState?.cupPartNumber}
								coneNumber={partState?.partNumber}
								series={partState?.series[0]}
								ca90RatingMetric={partState?.ca90RatingMetric}
								ca90RatingImperial={partState?.ca90RatingImperial}
								c90RatingMetric={partState?.c90RatingMetric}
								c90RatingImperial={partState?.c90RatingImperial}
								unitSystem={formState?.unitSystem}
								data={chartState.graphType === XAxisTypes.radial ? chartState.radialGraphData : chartState.axialGraphData}
								chartState={chartState}
								xRadioType={xRadioType}
								yRadioType={yRadioType}
								preload={Boolean(parseInt(formState?.springRatesPreload))}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};
export default BearingSpringRatesChartForm;
